import { useState } from 'react';
import { toast } from 'sonner';

import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Separator } from '~shared/shadcn/ui/separator';

import { useSkipFormSubmission } from '../api/submission-actions';
import {
  formatCrmResolutionLabel,
  getCrmResolutionTip,
} from '../lib/crm-resolution-labels';
import type {
  FormSubmission,
  FormSyncChannel,
  FormSyncStatus,
} from '../api/types';
import { SyncStatusBadge } from './sync-status-badge';

const CHANNEL_LABEL: Record<FormSyncChannel, string> = {
  portal: 'Portal',
  ghl: 'CRM',
};

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className='text-xs font-medium uppercase text-muted-foreground'>
        {label}
      </div>
      <div className='break-words text-sm'>{value ?? '—'}</div>
    </div>
  );
}

function ChannelBlock({
  channel,
  status,
  onSkip,
  skipPending,
  details,
}: {
  channel: FormSyncChannel;
  status: FormSyncStatus;
  onSkip: (channel: FormSyncChannel, status: FormSyncStatus) => void;
  skipPending: boolean;
  details: React.ReactNode;
}) {
  return (
    <div>
      <div className='mb-1 flex items-center gap-2'>
        <span className='text-xs font-medium uppercase text-muted-foreground'>
          {CHANNEL_LABEL[channel]} sync
        </span>
        <SyncStatusBadge status={status} />
        {status === 'failed' || status === 'pending' ? (
          <button
            type='button'
            disabled={skipPending}
            onClick={() => {
              onSkip(channel, status);
            }}
            className='cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50'
          >
            Skip
          </button>
        ) : null}
      </div>
      <div className='space-y-1 text-sm'>{details}</div>
    </div>
  );
}

export function FormSubmissionDetailsModal({
  submission,
  open,
  onOpenChange,
}: {
  submission: FormSubmission | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { formatInStudioTz } = useStudioDateFormatter();
  const skipMutation = useSkipFormSubmission();
  const [pendingConfirm, setPendingConfirm] = useState<FormSyncChannel | null>(
    null,
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setPendingConfirm(null);
    onOpenChange(nextOpen);
  };

  if (!submission) return null;

  const { sync } = submission;

  const doSkip = (channel: FormSyncChannel) => {
    skipMutation.mutate(
      { id: submission._id, channel },
      {
        onSuccess: () =>
          toast.success(`${CHANNEL_LABEL[channel]} sync marked as skipped`),
        onError: () =>
          toast.error(
            `Could not skip the ${CHANNEL_LABEL[channel]} sync. Try again.`,
          ),
      },
    );
  };

  // Failed channels skip directly; a pending channel is still healthy work in
  // flight, so it requires an explicit confirmation.
  const handleChannelSkip = (
    channel: FormSyncChannel,
    status: FormSyncStatus,
  ) => {
    if (status === 'failed') {
      doSkip(channel);
    } else {
      setPendingConfirm(channel);
    }
  };

  const attributionEntries = Object.entries(
    submission.attribution ?? {},
  ).filter(([, v]) => v);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-h-[85vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{submission.formName} submission</DialogTitle>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-4'>
          <Field
            label='Submitted'
            value={
              submission.submittedAt
                ? formatInStudioTz(submission.submittedAt)
                : '—'
            }
          />
          <Field label='Form type' value={submission.formType} />
          <Field
            label='Parent'
            value={`${submission.parent.firstName} ${submission.parent.lastName}`.trim()}
          />
          <Field label='Email' value={submission.parent.email} />
          <Field label='Phone' value={submission.parent.phone} />
          <Field label='Student' value={submission.child?.firstName} />
          <Field label='Interest' value={submission.sessionInterest} />
          <Field label='Consent' value={submission.consent ? 'Yes' : 'No'} />
        </div>

        <Separator />

        <div className='grid grid-cols-2 gap-4'>
          <ChannelBlock
            channel='portal'
            status={sync.portalStatus}
            onSkip={handleChannelSkip}
            skipPending={skipMutation.isPending}
            details={
              <>
                <div>Attempts: {sync.portalAttempts ?? 0}</div>
                {sync.portalLeadId ? (
                  <div>Lead: {sync.portalLeadId}</div>
                ) : null}
                {sync.portalLastError ? (
                  <div className='break-words text-red-600'>
                    {sync.portalLastError}
                  </div>
                ) : null}
              </>
            }
          />
          <ChannelBlock
            channel='ghl'
            status={sync.ghlStatus}
            onSkip={handleChannelSkip}
            skipPending={skipMutation.isPending}
            details={
              <>
                <div>Attempts: {sync.ghlAttempts ?? 0}</div>
                {sync.ghlContactId ? (
                  <div>Contact: {sync.ghlContactId}</div>
                ) : null}
                {sync.ghlResolution ? (
                  <div title={getCrmResolutionTip(sync.ghlResolution) ?? undefined}>
                    CRM result:{' '}
                    {formatCrmResolutionLabel(sync.ghlResolution)}
                  </div>
                ) : null}
                {sync.ghlLastError ? (
                  <div className='break-words text-red-600'>
                    {sync.ghlLastError}
                  </div>
                ) : null}
              </>
            }
          />
        </div>

        {attributionEntries.length ? (
          <>
            <Separator />
            <div>
              <div className='mb-2 text-xs font-medium uppercase text-muted-foreground'>
                Attribution
              </div>
              <div className='grid grid-cols-2 gap-2'>
                {attributionEntries.map(([key, value]) => (
                  <Field key={key} label={key} value={value} />
                ))}
              </div>
            </div>
          </>
        ) : null}

        {submission.rawPayload && Object.keys(submission.rawPayload).length ? (
          <>
            <Separator />
            <div>
              <div className='mb-2 text-xs font-medium uppercase text-muted-foreground'>
                Raw payload
              </div>
              <pre className='max-h-56 overflow-auto rounded bg-muted p-3 text-xs'>
                {JSON.stringify(submission.rawPayload, null, 2)}
              </pre>
            </div>
          </>
        ) : null}
      </DialogContent>

      <AlertDialog
        open={pendingConfirm !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Skip pending {pendingConfirm ? CHANNEL_LABEL[pendingConfirm] : ''}{' '}
              sync?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This sync has not failed. Skipping it means the submission stays
              saved in Enrollio but will never be sent to{' '}
              {pendingConfirm ? CHANNEL_LABEL[pendingConfirm] : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep sync</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingConfirm) doSkip(pendingConfirm);
                setPendingConfirm(null);
              }}
            >
              Skip sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
